import React from "react";
import clsx from "clsx";

import { ReactComponent as DownloadIcon } from "../../../assets/svg/download.svg";
import oppaSheetImg from "../../../assets/images/oppa_sheet.png";
import Spinner from "../../../atoms/Spinner";
import Sidebar from "../../../organisms/teacher/Sidebar";

import styles from "./DownloadAllPortfolio.module.scss";

interface IPortfolioItem {
  name: string;
  title: string;
  materialId: number;
  package_version: string | null;
}

export type Props = {
  calling: boolean;
  middleItems: IPortfolioItem[];
  highItems: IPortfolioItem[];
  otherItems: IPortfolioItem[];
  goBack: () => void;
  onDownloadMaterial: (materialId: number) => void;
};

type SchoolType = "middle" | "high";

export const DownloadAllPortfolio: React.FC<Props> = ({
  calling,
  middleItems,
  highItems,
  otherItems,
  goBack,
  onDownloadMaterial,
}) => {
  const hasBoth = middleItems.length > 0 && highItems.length > 0;
  const [schoolType, setSchoolType] = React.useState<SchoolType>("middle");

  const mountedRef = React.useRef(false);
  const [hasLoaded, setHasLoaded] = React.useState(false);
  React.useEffect(() => {
    if (mountedRef.current) {
      setHasLoaded(true);
    }
    mountedRef.current = true;
  }, [middleItems, highItems, otherItems]);

  const isMiddle = hasBoth ? schoolType === "middle" : middleItems.length > 0;

  const coreItems = isMiddle ? middleItems : highItems;
  const items = coreItems.length > 0 ? coreItems : otherItems;

  const showVersionHeader = React.useMemo(
    () => new Set(items.map(i => i.package_version).filter(Boolean)).size >= 2,
    [items],
  );

  return (
    <div className={styles.pageLayout}>
      {!hasLoaded ? (
        <div className={styles.loading}>
          <Spinner spinColor="black" />
          <p className={styles.loadingText}>教材取得中...</p>
        </div>
      ) : (
        <>
          <Sidebar
            classList={[]}
            currentMaterials={[]}
            viewingMaterialId={null}
            activeModalId={null}
            activeTab="core"
            targetClassIds={[]}
            showTabs={false}
            hasMiddle={false}
            hasHigh={false}
            hasCore={false}
            hasBase={false}
            backLabel="データ選択に戻る"
            onBackToClass={goBack}
            onTabClick={() => undefined}
            onItemClick={() => undefined}
          />

          <div className={styles.main}>
            <div className={styles.innerContent}>
              <h2 className={styles.pageTitle}>振り返りシート</h2>

              <div className={styles.imageCard}>
                <img
                  src={oppaSheetImg}
                  alt="振り返りシート"
                  className={styles.oppaImage}
                />
                <p className={styles.imageCardDesc}>
                  vol.ごとの生徒の学びと変化を1枚にまとめたPDFデータです。
                </p>
              </div>

              {hasBoth && (
                <div className={styles.radioGroup}>
                  {(["middle", "high"] as SchoolType[]).map(type => (
                    <div
                      key={type}
                      className={clsx(
                        styles.radioItem,
                        schoolType === type && styles.radioActive,
                      )}
                      onClick={() => setSchoolType(type)}
                    >
                      <div className={styles.radioCircle} />
                      <span>{type === "middle" ? "中学" : "高校"}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className={styles.lessonList}>
                {items.map((item, index) => {
                  const prevVersion =
                    index > 0 ? items[index - 1].package_version : null;
                  const showHeader =
                    showVersionHeader &&
                    item.package_version &&
                    item.package_version !== prevVersion;
                  return (
                    <React.Fragment key={item.materialId}>
                      {showHeader && (
                        <div className={styles.versionHeader}>
                          ver {item.package_version}
                        </div>
                      )}
                      <div
                        className={clsx(
                          styles.lessonItem,
                          calling && styles.disabled,
                        )}
                        onClick={() => onDownloadMaterial(item.materialId)}
                      >
                        <div className={styles.downloadIconArea}>
                          <DownloadIcon className={styles.downloadIcon} />
                        </div>
                        <div className={styles.lessonTextArea}>
                          <span className={styles.lessonVol}>
                            {item.name.trim()}
                          </span>
                          <span className={styles.lessonTitle}>
                            {item.title.replace(/\s*-高校-|\s*-中学-/g, "")}
                          </span>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DownloadAllPortfolio;
